import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaGem, FaUserFriends, FaMedal } from "react-icons/fa";

const AboutSection = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: false });
  const [counts, setCounts] = useState({
    clients: 0,
    years: 0,
    customDesigns: 0,
  });
  const countRef = useRef(null);

  // Animate elements when they come into view
  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  // Counter animation
  useEffect(() => {
    if (inView && countRef.current === null) {
      countRef.current = true;

      // Clients counter (0 to 1000+)
      const clientsInterval = setInterval(() => {
        setCounts((prev) => {
          const newCount = prev.clients + 25;
          if (newCount >= 1000) {
            clearInterval(clientsInterval);
            return { ...prev, clients: 1000 };
          }
          return { ...prev, clients: newCount };
        });
      }, 20);

      // Years counter (0 to 8+)
      const yearsInterval = setInterval(() => {
        setCounts((prev) => {
          const newCount = prev.years + 1;
          if (newCount >= 8) {
            clearInterval(yearsInterval);
            return { ...prev, years: 8 };
          }
          return { ...prev, years: newCount };
        });
      }, 200);

      // Custom designs counter (0 to 100%)
      const designsInterval = setInterval(() => {
        setCounts((prev) => {
          const newCount = prev.customDesigns + 5;
          if (newCount >= 100) {
            clearInterval(designsInterval);
            return { ...prev, customDesigns: 100 };
          }
          return { ...prev, customDesigns: newCount };
        });
      }, 40);

      return () => {
        clearInterval(clientsInterval);
        clearInterval(yearsInterval);
        clearInterval(designsInterval);
      };
    }
  }, [inView]);

  const variants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <AboutContainer id="about" ref={ref}>
      <BackgroundGlow className="parallax" data-speed="30" />

      <AboutContent>
        <SectionHeader>
          <SectionSubtitle
            initial="hidden"
            animate={controls}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6 },
              },
            }}
          >
            Who We Are
          </SectionSubtitle>

          <SectionTitle
            initial="hidden"
            animate={controls}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.2 },
              },
            }}
          >
            About Us
          </SectionTitle>

          <SectionDescription
            initial="hidden"
            animate={controls}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.4 },
              },
            }}
          >
            Baltimore Grillz & Custom Jewelry stands as the premier destination
            for luxury custom grillz and jewelry creations throughout Baltimore
            and beyond.
          </SectionDescription>
        </SectionHeader>

        <AboutGrid>
          <AboutTextColumn
            initial="hidden"
            animate={controls}
            variants={variants}
            custom={0}
          >
            <AboutTitle>Crafting Excellence Since 2015</AboutTitle>
            <AboutParagraph>
              Welcome to Baltimore Grillz & Custom Jewelry, where exquisite
              craftsmanship meets personal expression. As the premier
              destination for high-end custom grillz and jewelry in Baltimore,
              our passion for detail and commitment to quality have established
              us as industry leaders.
            </AboutParagraph>
            <AboutParagraph>
              Each creation is meticulously handcrafted using only the finest
              materials, from premium gold and platinum to ethically sourced
              diamonds and gemstones. Our team of skilled artisans combines
              traditional techniques with innovative designs to deliver
              exceptional pieces that stand the test of time.
            </AboutParagraph>

            <StatsContainer>
              <StatItem
                initial="hidden"
                animate={controls}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, delay: 0.6 },
                  },
                }}
              >
                <StatIcon>
                  <FaUserFriends />
                </StatIcon>
                <StatNumber>{counts.clients}+</StatNumber>
                <StatText>Satisfied Clients</StatText>
              </StatItem>

              <StatItem
                initial="hidden"
                animate={controls}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, delay: 0.7 },
                  },
                }}
              >
                <StatIcon>
                  <FaMedal />
                </StatIcon>
                <StatNumber>{counts.years}+</StatNumber>
                <StatText>Years of Experience</StatText>
              </StatItem>

              <StatItem
                initial="hidden"
                animate={controls}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, delay: 0.8 },
                  },
                }}
              >
                <StatIcon>
                  <FaGem />
                </StatIcon>
                <StatNumber>{counts.customDesigns}%</StatNumber>
                <StatText>Custom Designs</StatText>
              </StatItem>
            </StatsContainer>
          </AboutTextColumn>

          <AboutImageColumn
            initial="hidden"
            animate={controls}
            variants={variants}
            custom={1}
          >
            <ImageWrapper>
              <AboutImage
                src="/images/335571427_155373320737332_967755093724042291_n.jpg"
                alt="Custom Grillz Workshop"
              />
              <ImageOverlay />
              <FloatingDiamond className="large" />
              <FloatingDiamond className="small" />
            </ImageWrapper>
          </AboutImageColumn>
        </AboutGrid>
      </AboutContent>
    </AboutContainer>
  );
};

// Styled Components
const AboutContainer = styled.section`
  background-color: var(--dark-bg);
  position: relative;
  overflow: hidden;
  padding: var(--spacing-xl) 0;
`;

const BackgroundGlow = styled.div`
  position: absolute;
  top: 10%;
  right: 0;
  width: 800px;
  height: 800px;
  background: radial-gradient(
    circle at center,
    rgba(255, 215, 0, 0.05) 0%,
    transparent 70%
  );
  z-index: 0;

  @media (max-width: 992px) {
    width: 500px;
    height: 500px;
  }
`;

// Keyframes for subtle glow pulse
const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.1);
  }
  50% {
    box-shadow: 0 0 30px rgba(255, 215, 0, 0.2);
  }
`;

const AboutContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 0 var(--spacing-sm);
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-lg);
`;

const SectionSubtitle = styled(motion.span)`
  color: var(--gold);
  text-transform: uppercase;
  letter-spacing: 4px;
  font-size: 0.9rem;
  margin-bottom: var(--spacing-xs);
  display: block;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  background: var(--gold-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: var(--spacing-sm);
  position: relative;
  display: inline-block;
  text-transform: uppercase;
  font-weight: 800;

  &::after {
    content: "";
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: var(--gold-gradient);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 576px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled(motion.p)`
  max-width: 800px;
  margin: var(--spacing-md) auto 0;
  font-size: 1.1rem;
  color: #d0d0d0;
  line-height: 1.8;

  @media (max-width: 576px) {
    font-size: 1rem;
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-lg);
  align-items: center;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const AboutTextColumn = styled(motion.div)`
  @media (max-width: 992px) {
    order: 2;
  }
`;

const AboutTitle = styled.h3`
  font-size: 2.2rem;
  background: var(--gold-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: var(--spacing-md);
  position: relative;
  line-height: 1.3;

  &::before {
    content: "";
    position: absolute;
    top: -10px;
    left: 0;
    width: 40px;
    height: 3px;
    background: var(--gold);
  }

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const AboutParagraph = styled.p`
  font-size: 1.1rem;
  margin-bottom: var(--spacing-md);
  line-height: 1.8;
  color: #d0d0d0;

  @media (max-width: 576px) {
    font-size: 1rem;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
`;

const StatItem = styled(motion.div)`
  text-align: center;
  padding: var(--spacing-md);
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 215, 0, 0.1);
  border-radius: var(--radius-lg);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover {
    background: rgba(255, 215, 0, 0.05);
    border-color: rgba(255, 215, 0, 0.3);
    transform: translateY(-5px);
    animation: ${glowPulse} 2s ease-in-out infinite;
  }
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: rgba(255, 215, 0, 0.05);
  border-radius: 50%;
  margin: 0 auto var(--spacing-sm);
  color: var(--gold);
  font-size: 1.5rem;
  border: 1px solid rgba(255, 215, 0, 0.1);
  transition: var(--transition-medium);

  ${StatItem}:hover & {
    background: var(--gold-gradient);
    color: var(--black);
    transform: scale(1.1);
    box-shadow: var(--gold-glow);
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  background: var(--gold-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 5px;
  line-height: 1;

  @media (max-width: 576px) {
    font-size: 2rem;
  }
`;

const StatText = styled.div`
  font-size: 0.9rem;
  color: var(--silver);
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
`;

const AboutImageColumn = styled(motion.div)`
  position: relative;

  @media (max-width: 992px) {
    order: 1;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.5);
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transform: perspective(1000px) rotateY(-10deg) rotateX(2deg);

  &:hover {
    transform: perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1.02);
    box-shadow: 0 40px 80px rgba(0, 0, 0, 0.6), 0 0 40px rgba(255, 215, 0, 0.1);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 2px solid rgba(255, 215, 0, 0.3);
    border-radius: var(--radius-lg);
    z-index: 2;
    opacity: 0.5;
    pointer-events: none;
    transition: all 0.5s ease;
  }

  &:hover::before {
    border-color: rgba(255, 215, 0, 0.6);
    opacity: 1;
  }

  &::after {
    content: "";
    position: absolute;
    top: 15px;
    left: 15px;
    width: calc(100% - 30px);
    height: calc(100% - 30px);
    border: 1px solid rgba(255, 215, 0, 0.2);
    border-radius: var(--radius-md);
    z-index: 2;
    opacity: 0.3;
    pointer-events: none;
  }
`;

const AboutImage = styled.img`
  width: 100%;
  height: 500px;
  object-fit: cover;
  filter: contrast(1.1) brightness(0.9);
  transition: all 0.7s ease;

  ${ImageWrapper}:hover & {
    filter: contrast(1.05) brightness(1.05);
  }

  @media (max-width: 576px) {
    height: 350px;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  z-index: 1;
  opacity: 0.7;
  transition: all 0.7s ease;

  ${ImageWrapper}:hover & {
    opacity: 0.4;
  }
`;

const FloatingDiamond = styled.div`
  position: absolute;
  background: rgba(255, 215, 0, 0.03);
  transform: rotate(45deg);
  border: 1px solid rgba(255, 215, 0, 0.1);
  z-index: 0;

  &.large {
    width: 120px;
    height: 120px;
    bottom: -60px;
    right: 30%;
  }

  &.small {
    width: 60px;
    height: 60px;
    top: 20%;
    left: -30px;
  }
`;

export default AboutSection;
